import { useState, useEffect, useCallback, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { useToast } from '../ui/use-toast';
import { Clock, Trash2, ChevronDown, ChevronRight, HandHelping, Loader2, Mountain, Route, Share2, UserPlus, Users } from 'lucide-react';
import { competitionRoundsService } from '../../services/competition-rounds';
import EnteredAthletes, { type EnteredAthlete } from '../guardian/EnteredAthletes';
import { inRound } from '../guardian/useGuardianWards';
import type { CompetitionRound } from '../../types/competition-round';
import type { RoundEntryOffer } from '../trophy/entry-options';
import { hasViewChoice, storeRoundView, type RoundView } from './round-views';
import { ShareRoundDialog } from './round-delegation/ShareRoundDialog';
import { delegationsFor, helperLandingPath } from './round-delegation/storage';
import { useAuth } from '../../hooks/useAuth';
import RoundSquadAthletes from './RoundSquadAthletes';
import { squadInRound, type TeamEntriesState } from './useTeamEntries';

interface CompetitionRoundsListProps {
  competitionId: string;
  competitionSlug: string;
  /**
   * The entries belonging to this account — the viewer's own and their
   * children's. A round runs named classes, so each card shows the ones whose
   * class it runs: that is where they are looked for, and where they can be
   * taken back off. The ones with no class yet are the page's to show, above
   * this list.
   */
  athletes?: EnteredAthlete[];
  onWardsChanged?: () => void;
  isAdmin: boolean;
  isJudge: boolean;
  /**
   * Whether this viewer runs or judges rounds here — staff or a judge. They
   * reach a round whatever state it is in; its athletes while it runs and is
   * verified, everybody else only while it is running (see `readable` below).
   */
  canScore?: boolean;
  /**
   * A squad's team manager's squads here (useTeamEntries); absent for anybody
   * else. They reach a round once it has started; before then there is nothing
   * on its page for them to do, and its card unfolds the squad's athletes in it
   * instead.
   */
  squad?: TeamEntriesState;
  /**
   * The cards the viewer can enter from — their category is known, so the
   * button is on the round(s) it climbs rather than at the top of the page
   * (see roundEntryOffers). A full turn's card shows none.
   */
  entryOffers?: RoundEntryOffer[];
  onEnterRound?: (offer: RoundEntryOffer) => void;
  /** The button's flow is getting ready. */
  entryBusy?: boolean;
  /** Re-reads the rounds when it changes: their seats follow the page's entries. */
  refreshKey?: unknown;
  /**
   * The ways this viewer may read each round (see `availableRoundViews`).
   * Somebody with more than one job here — an admin who also judges, a team
   * manager who also climbs — gets a button per view on the round's card.
   */
  roundViews?: (round: CompetitionRound) => RoundView[];
}

export default function CompetitionRoundsList({
  competitionId,
  competitionSlug,
  isAdmin,
  isJudge,
  canScore = false,
  squad,
  athletes = [],
  onWardsChanged,
  entryOffers = [],
  onEnterRound,
  entryBusy = false,
  refreshKey = null,
  roundViews,
}: CompetitionRoundsListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const [rounds, setRounds] = useState<CompetitionRound[]>([]);
  const [loading, setLoading] = useState(true);
  const [roundToDelete, setRoundToDelete] = useState<CompetitionRound | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  /** The round whose "share" dialog is open. */
  const [sharing, setSharing] = useState<CompetitionRound | null>(null);
  /**
   * The rounds this phone took a helper's link for — read from the browser, so
   * it is there signed out too, and whatever state the round is in: the link is
   * how the helper gets back to work (see round-delegation/storage).
   */
  const [helping] = useState(() => delegationsFor(competitionSlug));
  /** The round whose card shows the team manager's athletes in it. */
  const [unfolded, setUnfolded] = useState<string | null>(null);

  const fetchRounds = useCallback(async () => {
    try {
      const data = await competitionRoundsService.getRounds(competitionId);
      setRounds(data);
    } catch (error) {
      console.error('Failed to fetch rounds:', error);
      toast({
        title: t('errors.loadFailed'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [competitionId, t, toast]);

  useEffect(() => {
    fetchRounds();
  }, [fetchRounds, refreshKey]);

  // Rounds are made, started and closed from the gym panel, not from this
  // list: it is the page the room reads, and the clock is not something to
  // press by mistake from a phone at the wall.

  const handleDeleteRound = async () => {
    if (!roundToDelete) return;

    setActionLoading(`delete-${roundToDelete.id}`);
    try {
      await competitionRoundsService.deleteRound(competitionId, roundToDelete.id);
      setRounds(prev => prev.filter(r => r.id !== roundToDelete.id));
      setRoundToDelete(null);

      toast({
        title: t('competitionRounds.deleted', { defaultValue: 'Round deleted' }),
      });
    } catch (error) {
      toast({
        title: t('errors.deleteFailed'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setActionLoading(null);
    }
  };

  /**
   * Whether this viewer may open the round.
   *
   * A round nobody has started is a plan — not a thing to walk into — so for the
   * room at large the way in appears when the clock starts. It opens again at the
   * other end: a **closed** round is its standings, which is the page everybody
   * wants when the afternoon is over, and it needs no account to read.
   *
   * What stays shut to the room is the hour in between: during verification the
   * cards are still being corrected, and a published order would be wrong in
   * front of the people it is about. The people who have marks to write reach a
   * round whatever state it is in, and have to: a judge's and a team manager's
   * work is exactly that hour (see Lifecycle::Round). So do the athletes who
   * climbed it, and the parents who mark for them, for as long as it lasts:
   * their own card is still theirs to put right through the corrections, and
   * theirs to read while the officials check it (`under_review`).
   *
   * A team manager has marks to write from the start on, not before: a round
   * that has not started has no card to mark, so its card unfolds the squad's
   * athletes in it rather than opening an empty page (`foldable`).
   */
  const marksOwnCard = (round: CompetitionRound) =>
    (roundViews?.(round) ?? []).some((view) => view === 'athlete' || view === 'team');
  const readable = (round: CompetitionRound) =>
    canScore ||
    round.running ||
    round.closed ||
    ((round.correcting || round.under_review) && (!!squad || marksOwnCard(round)));
  const foldable = (round: CompetitionRound) => !!squad && !readable(round);

  const toggleSquad = (round: CompetitionRound) =>
    setUnfolded((current) => (current === round.id ? null : round.id));

  const handleRoundClick = (round: CompetitionRound) => {
    navigate(`/competitions/${competitionSlug}/rounds/${round.id}`);
  };

  /** The round, read one particular way — remembered for the next one. */
  const openRoundAs = (round: CompetitionRound, view: RoundView) => {
    storeRoundView(user?.id, competitionSlug, view);
    navigate(`/competitions/${competitionSlug}/rounds/${round.id}?view=${view}`);
  };

  /**
   * The views worth offering on a card: only when there are two jobs to choose
   * between. Somebody with one has the row itself, which opens on it; the
   * spectator's view comes along as one more choice, never as a reason.
   */
  const viewChoices = (round: CompetitionRound): RoundView[] => {
    const views = roundViews?.(round) ?? [];
    return hasViewChoice(views) ? views : [];
  };

  const getStatusBadge = (round: CompetitionRound) => {
    if (round.running) {
      return <Badge variant="default" className="bg-green-600">{t('competitionRounds.active')}</Badge>;
    }
    // Over on the wall, still open to everybody who scores it.
    if (round.correcting) {
      return <Badge variant="default" className="bg-amber-600">{t('competitionRounds.corrections')}</Badge>;
    }
    // Being checked: the officials' alone.
    if (round.under_review) {
      return <Badge variant="default" className="bg-amber-700">{t('competitionRounds.verification')}</Badge>;
    }
    if (round.ended) {
      return <Badge variant="secondary">{t('competitionRounds.finished')}</Badge>;
    }
    // No badge for a round nobody has started. "Draft" is the panel's word for
    // work in progress, and on the page the room reads it says nothing a visitor
    // wants: a round that has not begun is simply the next one, which the start
    // time beside it already tells them.
    return null;
  };

  const getRoundTypeIcon = (type: string) => {
    if (type.includes('boulder')) {
      return <Mountain className="h-5 w-5" />;
    }
    return <Route className="h-5 w-5" />;
  };

  /**
   * When the round begins — the time somebody deciding whether to come, or
   * waiting to climb, actually wants. Before the start it is the planned
   * time; afterwards, when it was pressed. Nothing when nobody has said.
   */
  const formatStart = (round: CompetitionRound) => {
    if (!round.start_time) return null;
    return new Date(round.start_time).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('competitionRounds.title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-12 w-12 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('competitionRounds.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {rounds.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Mountain className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{t('competitionRounds.noRounds')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {rounds.map((round) => (
                <div key={round.id} className="rounded-lg border" data-testid="round-card">
                <div
                  className={`flex items-center gap-4 p-4 transition-colors ${
                    readable(round) || foldable(round) ? 'hover:bg-muted/50 cursor-pointer' : ''
                  }`}
                  onClick={() => {
                    if (readable(round)) handleRoundClick(round);
                    else if (foldable(round)) toggleSquad(round);
                  }}
                  {...(foldable(round) ? {
                    role: 'button',
                    tabIndex: 0,
                    'aria-expanded': unfolded === round.id,
                    onKeyDown: (event: KeyboardEvent) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return;
                      event.preventDefault();
                      toggleSquad(round);
                    },
                  } : {})}
                  data-testid="round-row"
                  data-readable={readable(round) ? 'true' : 'false'}
                >
                  <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                    {getRoundTypeIcon(round.type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold truncate">{round.name}</h3>
                      {getStatusBadge(round)}
                    </div>
                    {/* The discipline, and nothing counted. How many problems a
                        round holds is the organiser's business — it is being
                        decided right up to the morning, and to the room it reads
                        as a fact about the round when it is not one yet. */}
                    <p className="text-sm text-muted-foreground">{round.round_type}</p>
                    {/* When it begins — the one fact a visitor came for. */}
                    {formatStart(round) && (
                      <p
                        className="flex items-center gap-1 text-sm text-muted-foreground"
                        data-testid="round-start-time"
                      >
                        <Clock className="h-3.5 w-3.5" aria-hidden />
                        {t(round.running || round.ended ? 'competitionRounds.startedAt' : 'competitionRounds.startsAt', {
                          time: formatStart(round),
                        })}
                      </p>
                    )}
                    {/* A turn with a limit, before it starts: how many seats
                        are left, which is what decides which one to book. */}
                    {round.bookable && round.seats_left != null && !round.running && !round.ended && (
                      <p
                        className={`flex items-center gap-1 text-sm ${
                          round.seats_left === 0 ? 'text-destructive' : 'text-muted-foreground'
                        }`}
                        data-testid="round-seats"
                      >
                        <Users className="h-3.5 w-3.5" aria-hidden />
                        {round.seats_left === 0
                          ? t('competitionRounds.full')
                          : t('competitionRounds.seatsLeft', { count: round.seats_left })}
                      </p>
                    )}
                    {/* A team manager's, before it starts: how many of theirs
                        climb it, the reason to unfold it. */}
                    {foldable(round) && squad?.page && (
                      <p
                        className="flex items-center gap-1 text-sm text-muted-foreground"
                        data-testid="round-squad-count"
                      >
                        <Users className="h-3.5 w-3.5" aria-hidden />
                        {t('competitionRounds.squad.count', {
                          count: squadInRound(squad.page.entries, round).length,
                        })}
                      </p>
                    )}
                  </div>

                  {(isAdmin || isJudge) && (
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {isAdmin && !round.running && !round.ended && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setRoundToDelete(round)}
                          disabled={actionLoading === `delete-${round.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  )}

                  {/* No chevron on a row that does not open: the arrow is the
                      promise that tapping does something. */}
                  {readable(round) && <ChevronRight className="h-5 w-5 text-muted-foreground" />}
                  {foldable(round) && (
                    <ChevronDown
                      className={`h-5 w-5 text-muted-foreground transition-transform ${
                        unfolded === round.id ? '' : '-rotate-90'
                      }`}
                      aria-hidden
                    />
                  )}
                </div>

                {squad && foldable(round) && unfolded === round.id && (
                  <div className="border-t px-4 py-3" data-testid="round-squad">
                    <RoundSquadAthletes
                      entries={squadInRound(squad.page?.entries ?? [], round)}
                      loading={squad.loading}
                      loadFailed={squad.loadFailed}
                      showTeam={(squad.page?.teams.length ?? 0) > 1}
                    />
                  </div>
                )}

                {/* Outside the clickable row on purpose: entering, or
                    withdrawing an entry, must not also open the round. */}
                {(() => {
                  const offer = entryOffers.find((candidate) => candidate.roundId === round.id);
                  if (!offer || offer.full || !onEnterRound) return null;
                  return (
                    <div className="border-t px-4 py-3" onClick={(event) => event.stopPropagation()}>
                      <Button
                        size="sm"
                        className="w-full gap-1.5 sm:w-auto"
                        disabled={entryBusy}
                        onClick={() => onEnterRound(offer)}
                        data-testid="round-enter"
                      >
                        {entryBusy ? (
                          <Loader2 className="size-4 animate-spin" aria-hidden />
                        ) : (
                          <UserPlus className="size-4" aria-hidden />
                        )}
                        {t(offer.bookRoundId ? 'stageEntry.enterTurn' : 'stageEntry.enterRound')}
                      </Button>
                    </div>
                  );
                })()}
                {/* Outside the clickable row too: each button opens the round
                    one particular way. */}
                {readable(round) && viewChoices(round).length > 0 && (
                  <div
                    className="flex flex-wrap items-center gap-2 border-t px-4 py-3"
                    onClick={(event) => event.stopPropagation()}
                    data-testid="round-views"
                  >
                    <span className="text-sm text-muted-foreground">{t('competitionRounds.views.label')}</span>
                    {viewChoices(round).map((view) => (
                      <Button
                        key={view}
                        size="sm"
                        variant="outline"
                        onClick={() => openRoundAs(round, view)}
                        data-testid={`round-view-${view}`}
                      >
                        {t(`competitionRounds.views.${view}`)}
                      </Button>
                    ))}
                  </div>
                )}
                {/* A helper's phone, back on the stage's page: the way to the
                    round it is scoring for somebody. Not subject to
                    `readable`: the room may not open a round in
                    verification, but the helper is not the room. */}
                {helping
                  .filter((entry) => entry.roundId === round.id)
                  .map((entry) => (
                    <div
                      key={entry.token}
                      className="border-t px-4 py-3"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Button
                        size="sm"
                        variant="secondary"
                        className="w-full gap-1.5 sm:w-auto"
                        onClick={() => navigate(helperLandingPath(entry))}
                        data-testid="round-helping"
                      >
                        <HandHelping className="size-4" aria-hidden />
                        {t('roundHelp.resume', { name: entry.grantorName })}
                      </Button>
                    </div>
                  ))}
                {/* Handing some cards of the round to somebody else's phone:
                    a squad's team manager or an athlete, while the round has
                    not stopped (the server's `delegable_kinds`). */}
                {(round.delegable_kinds?.length ?? 0) > 0 && (
                  <div className="border-t px-4 py-3" onClick={(event) => event.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full gap-1.5 sm:w-auto"
                      onClick={() => setSharing(round)}
                      data-testid="round-share"
                    >
                      <Share2 className="size-4" aria-hidden />
                      {t('roundHelp.share.button')}
                    </Button>
                  </div>
                )}
                <div onClick={(event) => event.stopPropagation()}>
                  <EnteredAthletes
                    entered={inRound(athletes, round)}
                    competitionSlug={competitionSlug}
                    onWithdrawn={() => onWardsChanged?.()}
                    className="border-t px-4 py-3"
                  />
                </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {sharing && (
        <ShareRoundDialog
          open
          onOpenChange={(next) => !next && setSharing(null)}
          competitionSlug={competitionSlug}
          roundId={sharing.id}
          roundName={sharing.name}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!roundToDelete} onOpenChange={() => setRoundToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('competitionRounds.deleteRound')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('competitionRounds.deleteRoundConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRound}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
